import React from 'react';

export function SemanticSearchResults({ results, query, onViewDetails, onSignInNeeded, onClearResults }) {
  if (!results || !results.similarRequests || results.similarRequests.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* No Results Found */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-blue-200 shadow-xl">
          <div className="text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">No Similar Requests Found</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              We couldn't find any requests similar to "{query}". This might be a unique idea!
            </p>
            <button
              onClick={onClearResults}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center mx-auto text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to All Requests
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sort results by similarity score (highest first)
  const sortedRequests = [...results.similarRequests].sort((a, b) => 
    (b.similarity_score || 0) - (a.similarity_score || 0)
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-purple-200 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Smart Search Results</h2>
            <p className="text-sm sm:text-base text-gray-600">
              Found {sortedRequests.length} request{sortedRequests.length !== 1 ? 's' : ''} similar to "{query}"
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              ⭐ Ordered by similarity (most relevant first)
            </p>
          </div>
          <button
            onClick={onClearResults}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to All Requests
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-3 sm:space-y-4">
        {sortedRequests.map((request, index) => (
          <div 
            key={request.id} 
            className="group bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 border-gray-200 hover:border-purple-300 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-[1.01]"
            onClick={() => onViewDetails && onViewDetails(request)}
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
                  {request.title}
                </h3>
                
                {/* Similarity Score */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                  <div className="flex items-center bg-gradient-to-r from-purple-100 to-pink-100 px-2.5 sm:px-3 py-1 rounded-full">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-xs sm:text-sm font-semibold text-purple-800">
                      {Math.round(request.similarity_score * 100)}% similar
                    </span>
                  </div>
                  
                  {/* Vote and Comment counts */}
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="flex items-center text-gray-600">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium">{request.vote_count || 0}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium">{request.comment_count || 0}</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-3">
                  {request.description}
                </p>
                
                {/* Author and date */}
                <div className="flex items-center text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                  <span>By {request.author_name || 'Anonymous'}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(request.created_at).toLocaleDateString()}</span>
                </div>

                {/* AI Reasoning */}
                {request.reasoning && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-blue-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-semibold text-blue-800 mb-1">Why this is similar:</p>
                        <p className="text-xs sm:text-sm text-blue-700">{request.reasoning}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-gray-200 shadow-xl text-center">
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Didn't find exactly what you need?</h3>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
          Vote on the requests above that are close to what you want, or submit your own unique request.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <button
            onClick={onClearResults}
            className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Browse All Requests
          </button>
          <button
            onClick={() => window.location.href = '/submit'}
            className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Submit New Request
          </button>
        </div>
      </div>
    </div>
  );
} 